import { useState, useEffect } from 'react';

import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';

import { useAuth } from '@/contexts/auth';

import { useAppErrorToast } from '@/hooks/use-app-error-toast';
import { useToast } from '@/hooks/use-toast';

import { authService } from '@/services/auth';
import { applyFormErrorDetails } from '@/lib/form-errors';

import { PartnerViewerSignupSuccess } from '@/components/PartnerViewerSignupSuccess';
import { SignupInvitationChecking } from '@/components/SignupInvitationChecking';
import { SignupInvitationInvalid } from '@/components/SignupInvitationInvalid';
import { SignupInvitationUnavailable } from '@/components/SignupInvitationUnavailable';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PasswordRules } from '@/components/PasswordRules';
import { useSignupInvitationToken } from '@/hooks/use-signup-invitation-token';
import { AuthBackground } from '@/components/AuthBackground';
import { AuthLegalNotice } from '@/components/AuthLegalNotice';

const signupSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string()
    .min(8, 'Senha deve ter pelo menos 8 caracteres')
    .max(16, 'Senha deve ter no máximo 16 caracteres')
    .regex(/[A-Z]/, 'Senha deve conter pelo menos uma letra maiúscula')
    .regex(/[0-9]/, 'Senha deve conter pelo menos um número')
    .regex(/[^A-Za-z0-9]/, 'Senha deve conter pelo menos um caractere especial'),
  confirmPassword: z.string().min(1, 'Confirmação de senha é obrigatória'),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Senhas não coincidem',
  path: ['confirmPassword'],
});

type SignupFormData = z.infer<typeof signupSchema>;

export default function Signup() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const { showErrorToast } = useAppErrorToast();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partnerViewerComplete, setPartnerViewerComplete] = useState(false);
  const {
    token,
    emailFromUrl,
    isChecking,
    isInvalid,
    isUnavailable,
    isResending,
    resendSent,
    resendInvitation,
  } = useSignupInvitationToken();

  const forgotPasswordHref = emailFromUrl
    ? `/forgot-password?email=${encodeURIComponent(emailFromUrl)}`
    : '/forgot-password';

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: { errors, isValid }
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
    mode: 'onChange'
  });

  const password = watch('password');

  useEffect(() => {
    if (emailFromUrl) {
      setValue('email', emailFromUrl);
    }
  }, [emailFromUrl, setValue]);

  const onSubmit = async (data: SignupFormData) => {
    try {
      setIsSubmitting(true);

      const signupData = {
        email: data.email,
        password: data.password,
        confirmationPassword: data.confirmPassword,
        token: token || undefined
      };

      const response = await authService.signup(signupData);

      if (response.role === 'PARTNER_VIEWER') {
        setPartnerViewerComplete(true);
        return;
      }

      try {
        await authService.login({
          email: data.email,
          password: data.password
        });
        await refreshUser();
      } catch (loginError) {
        console.error('Error during auto-login:', loginError);
      }

      toast({
        title: 'Conta criada com sucesso!',
        description: 'Bem-vindo ao Culture Monitor!',
        variant: 'success',
      });

      navigate('/app/reports');
    } catch (error: unknown) {
      const normalizedError = showErrorToast(error, {
        title: 'Erro ao criar conta',
        fallbackMessage: 'Não foi possível criar sua conta. Por favor, tente novamente.',
      });
      applyFormErrorDetails(normalizedError.details, setError);
      console.error('Error creating account:', normalizedError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (partnerViewerComplete) {
    return <PartnerViewerSignupSuccess />;
  }

  return (
    <div className="min-h-screen bg-highlight flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative">
      {!isChecking && !isUnavailable && <AuthBackground />}
      <div className="relative z-10">
        {isChecking ? (
          <SignupInvitationChecking />
        ) : isUnavailable ? (
          <SignupInvitationUnavailable forgotPasswordHref={forgotPasswordHref} />
        ) : isInvalid ? (
          <SignupInvitationInvalid
            emailFromUrl={emailFromUrl}
            isResending={isResending}
            resendSent={resendSent}
            onResend={resendInvitation}
          />
        ) : (
          <div className="bg-white rounded-lg border p-6 flex flex-col w-full sm:w-96">
            <div className="text-center mb-6">
              <h1 className="text-xl font-sans font-semibold text-gray-900 mb-2 text-center leading-none">
                Bem-vindo :)
              </h1>
              <p className="text-sm font-sans font-normal text-muted-foreground text-center leading-5">
                Estamos quase lá! Só precisamos de algumas
                <br />
                informações para ativar sua conta.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  E-mail
                </label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  className="w-full"
                  placeholder="Digite seu email"
                  error={errors.email?.message}
                  disabled={!!emailFromUrl}
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Senha
                </label>
                <Input
                  id="password"
                  type="password"
                  {...register('password')}
                  placeholder="Digite sua senha"
                  error={errors.password?.message}
                />
                <PasswordRules
                  password={password}
                  rules={{
                    '[A-Z]': 'Pelo menos uma letra maiúscula',
                    '[0-9]': 'Pelo menos um número',
                    '.{8,}': 'Pelo menos 8 caracteres',
                    '.{0,16}': 'Máximo 16 caracteres',
                    '[^A-Za-z0-9]': 'Pelo menos um caractere especial'
                  }}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirme sua Senha
                </label>
                <Input
                  id="confirmPassword"
                  type="password"
                  {...register('confirmPassword')}
                  placeholder="Digite sua senha"
                  error={errors.confirmPassword?.message}
                />
              </div>
              <AuthLegalNotice />
              <Button
                type="submit"
                disabled={!isValid || isSubmitting}
                className="w-full bg-black text-white hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Criando conta...' : 'Continuar'}
              </Button>
            </form>
            <div className="mt-6 text-center">
              <span className="text-sm text-black">
                Já possui uma conta? {' '}
              </span>
              <Link
                to="/login"
                className="text-sm text-black underline"
              >
                Entrar.
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
